/**
 * The confirm between a recruiter's manual Send and the email modal, shown
 * only when automation has already emailed this VoE or has an email queued.
 *
 * "Send anyway" proceeds exactly as Send did before; the automated steps are
 * left alone and keep running. "Let it run" (or dismissing the prompt) sends
 * nothing. Both the prompt and the decision are reported to RUM so we can see
 * how often the confirm changes what recruiters do.
 */
import { useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import {
  describeAutomatedEmails,
  type AutomatedEmailConfirm,
  type AutomatedEmailSummary,
} from './useAutomatedEmailActions'

export type ManualSendOutcome = 'sent_anyway' | 'let_it_run'

export const MANUAL_SEND_CONFIRM_TITLE =
  'Automation already emailed this employer'
/** When nothing has gone out yet: the title must not claim a send. */
export const MANUAL_SEND_QUEUED_TITLE =
  'Automation has an email to this employer scheduled'

const eventFields = (voeId: string, confirm: AutomatedEmailConfirm) => ({
  surface: 'dashboard',
  component: 'VoEDetails',
  automated_sent_count: confirm.automatedSentCount,
  has_scheduled: confirm.hasScheduled,
  voe_id: voeId,
})

export const useManualSendConfirm = ({
  voeId,
  email,
  automationOn,
  summary,
  fetchLatestSummary,
}: {
  voeId: string | null | undefined
  email: string | null | undefined
  /** The company's chain is enabled. Off means Send never asks. */
  automationOn: boolean
  /** What the drawer loaded — the fallback when the re-check fails. */
  summary: AutomatedEmailSummary
  /**
   * Reads the automated emails from the server at the moment Send is
   * clicked. The drawer's copy can be minutes old, and automation sends right
   * after an email is captured, so deciding from it let a recruiter email an
   * employer seconds after automation had.
   */
  fetchLatestSummary?: () => Promise<AutomatedEmailSummary>
}) => {
  const [opened, setOpened] = useState(false)
  // Kept after close so the description does not blank during the exit
  // animation; the ref is what a decision reads, so one prompt is one event.
  const [confirm, setConfirm] = useState<AutomatedEmailConfirm | null>(null)
  const pendingRef = useRef<{
    confirm: AutomatedEmailConfirm
    proceed: () => void
  } | null>(null)

  const checkingRef = useRef(false)

  /** Run `proceed` now, or after the recruiter confirms when automation has emailed. */
  const requestSend = async (proceed: () => void) => {
    if (!automationOn || !voeId) {
      proceed()
      return
    }
    // One check per click: a second click while it runs is ignored.
    if (checkingRef.current) return
    checkingRef.current = true

    let latest = summary
    try {
      if (fetchLatestSummary) latest = await fetchLatestSummary()
    } catch {
      // Keep what the drawer loaded rather than blocking the send.
    } finally {
      checkingRef.current = false
    }

    const next = describeAutomatedEmails(latest, email)
    if (!next.shouldConfirm) {
      proceed()
      return
    }
    pendingRef.current = { confirm: next, proceed }
    setConfirm(next)
    setOpened(true)
    datadogRum.addAction('voe_manual_send_prompted', eventFields(voeId, next))
  }

  const decide = (outcome: ManualSendOutcome) => {
    const pending = pendingRef.current
    if (!pending || !voeId) return
    pendingRef.current = null
    setOpened(false)
    datadogRum.addAction('voe_manual_send_decided', {
      ...eventFields(voeId, pending.confirm),
      outcome,
    })
    if (outcome === 'sent_anyway') pending.proceed()
  }

  return {
    requestSend,
    /** Spread onto DnPrompt. Closing it any other way counts as "Let it run". */
    promptProps: {
      opened,
      title:
        confirm && confirm.automatedSentCount === 0
          ? MANUAL_SEND_QUEUED_TITLE
          : MANUAL_SEND_CONFIRM_TITLE,
      description: confirm?.description ?? '',
      confirmText: 'Send anyway',
      cancelText: 'Let it run',
      variant: 'warning' as const,
      onConfirm: () => decide('sent_anyway'),
      close: () => decide('let_it_run'),
    },
  }
}
